import { useInfiniteQuery } from '@tanstack/react-query';
import axiosClient from '@/lib/auth';
import { AlertType, MonitorAlertsSearched } from '../types';
import { sortAlerts } from '../utils';
import { useAuthStore } from '@/store/authStore';

const ALERTS_QUERY_KEY = 'alerts';
const POLL_INTERVAL = 5000; // 5 seconds

const getFilterParams = (type: AlertType, pageNumber: number = 1, pageSize: number = 20) => {
  const baseParams = {
    pageNumber,
    pageSize,
    filterOperator: 'And',
  };

  switch (type) {
    case 'new':
      return {
        ...baseParams,
      };
    case 'assigned':
      return {
        ...baseParams,
        'Filter.Filters[0][action]': 'Eq',
        'Filter.Filters[0][field]': 'closedAt',
        'Filter.Filters[1][action]': 'NEq',
        'Filter.Filters[1][field]': 'actionedBy',
      };
    case 'resolved':
      return {
        ...baseParams,
        'Filter.Filters[0][action]': 'NEq',
        'Filter.Filters[0][field]': 'closedAt',
      };
  }
};

export const useAlertsV2 = (
  type: AlertType,
  pageSize: number = 20,
  onCountChange?: (count: number) => void,
  sortingVersion: string = 'default'
) => {
  return useInfiniteQuery({
    queryKey: [ALERTS_QUERY_KEY, type, pageSize, sortingVersion],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      // Use a smaller page size for the first page to load faster
      const actualPageSize = pageParam === 1 ? Math.min(10, pageSize) : pageSize;

      const response = await axiosClient.get<MonitorAlertsSearched>('/AlertMonitoring/searchv2', {
        params: getFilterParams(type, pageParam, actualPageSize),
      });

      const alerts = response.data.rows || [];
      const sortedAlerts = alerts.filter(
        (alert, index, self) => index === self.findIndex((a) => a.id === alert.id)
      );

      if (onCountChange) {
        onCountChange(response.data.totalRows || 0);
      }

      return {
        alerts: sortedAlerts.map((alert) => ({
          id: alert.id,
          alertId: alert.alertId,
          companyName: alert.companyName,
          projectName: alert.projectName,
          occurred: alert.occurred,
          status: alert.status,
          snapshot: alert.snapshot,
          actionedBy: alert.actionedBy,
          lastEvent: alert.lastEvent,
        })),
        nextPage:
          (response.data.totalRows || 0) > pageParam * actualPageSize ? pageParam + 1 : undefined,
        totalRows: response.data.totalRows || 0,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    refetchInterval: POLL_INTERVAL,
    staleTime: POLL_INTERVAL,
    notifyOnChangeProps: ['data', 'error'],
  });
};
